import React, { useState, useEffect, useRef } from 'react';
import { Button, Alert, Text, Stack, TextInput, Checkbox, Grid, Group } from '@mantine/core';
import { IconLock } from '@tabler/icons-react';
import { getSquarePayments } from '../../lib/square';
import { postApi } from '../../lib/api';

/**
 * SquarePaymentForm Component
 * Collects the card and billing address and asks /api/create-square-payment to
 * charge. The server prices the registration itself; `quote` (from
 * /api/price-quote) is only what we show on the button. When a promo brings the
 * total to $0 the card is hidden and the server completes the registration
 * without charging.
 */
const SquarePaymentForm = ({
  quote,
  appliedPromo, // { code } once the server has accepted a promo code
  registrationId,
  dancerInfo,
  onApplyPromo, // async (code) => throws Error(message) if the code is rejected
  onRemovePromo,
  onSuccess,
  onError
}) => {
  const [card, setCard] = useState(null);
  // Required before paying, so every dancer explicitly confirms the refund policy
  // (useful if a charge is disputed). Not stored; the checkout simply requires it.
  const [agreedToRefundPolicy, setAgreedToRefundPolicy] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const cardContainerRef = useRef(null);

  // Billing Address State
  const [useSameAddress, setUseSameAddress] = useState(true);
  const [billingAddress, setBillingAddress] = useState({
    line1: dancerInfo?.address_street || '',
    line2: '',
    city: dancerInfo?.address_city || '',
    state: dancerInfo?.address_province || '',
    postal_code: dancerInfo?.address_postal_code || '',
    country: dancerInfo?.address_country === 'Canada' ? 'CA' : (dancerInfo?.address_country || 'CA')
  });

  // Promo code input state (the applied code itself lives in the parent's quote)
  const [couponCode, setCouponCode] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState(null);
  const [showCouponInput, setShowCouponInput] = useState(false);

  const total = quote ? quote.total : null;
  // Every checkout is a card charge; Square's minimum is $1.00. Comp codes are
  // set up to leave a few dollars, so this only shows if a code is misconfigured.
  const belowMinimum = total !== null && total < 1;
  const BELOW_MINIMUM_MESSAGE = 'The total must be at least $1.00 to pay by card. If a promo code brought it below that, please email info@prixducanada.com.';
  const NO_ANSWER_MESSAGE = "We couldn't confirm your payment because the connection dropped. Please don't pay again yet: "
    + 'refresh this page to see if it went through, and check your email for a receipt. '
    + 'If it still shows unpaid, you can try again.';

  // Initialize Square Card
  useEffect(() => {
    const initializeCard = async () => {
      try {
        // Wait for ref to be ready
        if (!cardContainerRef.current) {
          console.log('⏳ Waiting for card container ref...');
          return;
        }

        console.log('✅ Card container ref ready');
        const payments = await getSquarePayments();

        // Create card with custom styling
        const cardInstance = await payments.card({
          style: {
            input: {
              fontSize: '16px',
            },
            'input::placeholder': {
              color: '#aab7c4',
            },
            '.message-text': {
              color: '#fa755a',
            },
          }
        });

        await cardInstance.attach(cardContainerRef.current);
        setCard(cardInstance);
        console.log('✅ Square Card initialized and attached');
      } catch (err) {
        console.error('❌ Failed to initialize Square Card:', err);
        showError('Failed to load payment form. Please refresh the page.');
      }
    };

    // Small delay to ensure ref is mounted
    const timer = setTimeout(() => {
      initializeCard();
    }, 100);

    // Cleanup
    return () => {
      clearTimeout(timer);
      if (card) {
        card.destroy();
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle "Use same address" checkbox
  const handleUseSameAddressChange = (checked) => {
    console.log('📍 Use same address changed to:', checked);
    console.log('📍 Dancer info:', dancerInfo);

    setUseSameAddress(checked);
    if (checked) {
      const populatedAddress = {
        line1: dancerInfo?.address_street || '',
        line2: '',
        city: dancerInfo?.address_city || '',
        state: dancerInfo?.address_province || '',
        postal_code: dancerInfo?.address_postal_code || '',
        country: dancerInfo?.address_country === 'Canada' ? 'CA' : (dancerInfo?.address_country || 'CA')
      };
      console.log('📍 Populating billing address:', populatedAddress);
      setBillingAddress(populatedAddress);
    }
  };

  // Handle billing address field changes
  const handleBillingAddressChange = (field, value) => {
    setBillingAddress(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // One error box: the page's when it passes onError, this form's own otherwise.
  const showError = (message) => (onError ? onError(message) : setError(message));

  // Apply promo code: the parent re-quotes with it and throws if it's rejected
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }

    setCouponLoading(true);
    setCouponError(null);
    try {
      await onApplyPromo(couponCode.trim().toUpperCase());
      setShowCouponInput(false);
    } catch (err) {
      setCouponError(err.message || 'Failed to validate promo code. Please try again.');
    } finally {
      setCouponLoading(false);
    }
  };

  // Remove applied promo code
  const handleRemoveCoupon = () => {
    setCouponCode('');
    setCouponError(null);
    onRemovePromo();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!quote) {
      showError('Still calculating your total. Please try again in a moment.');
      return;
    }
    if (belowMinimum) {
      showError(BELOW_MINIMUM_MESSAGE);
      return;
    }
    if (!card) {
      showError('Payment form not ready. Please try again.');
      return;
    }

    // Ensure billing address is populated from dancerInfo if "Use same address" is checked
    let finalBillingAddress = billingAddress;
    if (useSameAddress) {
      finalBillingAddress = {
        line1: dancerInfo?.address_street || '',
        line2: '',
        city: dancerInfo?.address_city || '',
        state: dancerInfo?.address_province || '',
        postal_code: dancerInfo?.address_postal_code || '',
        country: dancerInfo?.address_country === 'Canada' ? 'CA' : (dancerInfo?.address_country || 'CA')
      };
    }

    if (!finalBillingAddress.line1 || !finalBillingAddress.city || !finalBillingAddress.postal_code) {
      showError('Please complete all required billing address fields.');
      return;
    }

    setProcessing(true);
    showError(null);

    try {
      const result = await card.tokenize();
      if (result.status !== 'OK') {
        console.error('❌ Tokenization failed:', result.errors);
        throw new Error(result.errors?.[0]?.message || 'Please check your card details and try again.');
      }

      let paymentData;
      try {
        paymentData = await postApi('/api/create-square-payment', {
          registrationId,
          sourceId: result.token,
          billingAddress: finalBillingAddress,
          promoCode: appliedPromo ? appliedPromo.code : null
        });
      } catch (err) {
        // No answer from our server (connection dropped, timeout): the card may
        // have been charged anyway, so don't invite a second payment.
        if (!err.status || /^Request failed/.test(err.message)) err.message = NO_ANSWER_MESSAGE;
        throw err;
      }
      console.log('✅ Payment successful:', paymentData.paymentId);

      await onSuccess(paymentData);
    } catch (err) {
      console.error('❌ Payment error:', err);
      showError(err.message || 'Payment failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Stack spacing="lg">
        {error && (
          <Alert color="red" title="Payment Error">
            {error}
          </Alert>
        )}

        {belowMinimum && (
          <Alert color="yellow" title="Nothing to charge">
            {BELOW_MINIMUM_MESSAGE}
          </Alert>
        )}

        {/* Square Card */}
        <div>
          <Text size="sm" weight={500} mb={8} style={{ textAlign: 'left', color: '#424770' }}>
            Card details
          </Text>
          <div
            ref={cardContainerRef}
            id="card-container"
            style={{
              minHeight: '56px',
              background: 'white',
            }}
          />
        </div>

        {/* Billing Address Section */}
        <div>
          <Text size="sm" weight={500} mb={8} style={{ textAlign: 'left', color: '#424770' }}>
            Billing Address
          </Text>

          <Checkbox
            label="Use my registration address"
            checked={useSameAddress}
            onChange={(e) => handleUseSameAddressChange(e.currentTarget.checked)}
            mb={useSameAddress ? 0 : "md"}
            styles={{
              label: {
                fontSize: '14px',
                lineHeight: '1.4'
              }
            }}
          />

          {!useSameAddress && (
            <Stack spacing="sm" mt="md">
              <TextInput
                label="Address Line 1"
                placeholder="Street address"
                value={billingAddress.line1}
                onChange={(e) => handleBillingAddressChange('line1', e.target.value)}
                required
              />

              <TextInput
                label="Address Line 2 (Optional)"
                placeholder="Apartment, suite, etc."
                value={billingAddress.line2}
                onChange={(e) => handleBillingAddressChange('line2', e.target.value)}
              />

              <Grid gutter="sm">
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <TextInput
                    label="City"
                    placeholder="City"
                    value={billingAddress.city}
                    onChange={(e) => handleBillingAddressChange('city', e.target.value)}
                    required
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <TextInput
                    label="Province/State"
                    placeholder="Province or State"
                    value={billingAddress.state}
                    onChange={(e) => handleBillingAddressChange('state', e.target.value)}
                  />
                </Grid.Col>
              </Grid>

              <Grid gutter="sm">
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <TextInput
                    label="Postal Code"
                    placeholder="e.g., M5H 2N2 or 90210"
                    value={billingAddress.postal_code}
                    onChange={(e) => handleBillingAddressChange('postal_code', e.target.value.toUpperCase())}
                    required
                    description={billingAddress.country === 'CA' ? 'Canadian format: A1A 1A1' : ''}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <TextInput
                    label="Country"
                    value={billingAddress.country}
                    onChange={(e) => handleBillingAddressChange('country', e.target.value)}
                  />
                </Grid.Col>
              </Grid>
            </Stack>
          )}
        </div>

        {/* Promo Code Section (Before Payment Button) */}
        <div>
          {appliedPromo && quote ? (
            <Alert color="green" withCloseButton onClose={handleRemoveCoupon}>
              <Group justify="space-between">
                <div>
                  <Text size="sm" weight={600}>{appliedPromo.code} Applied!</Text>
                  <Text size="sm" color="dimmed">
                    {`You save $${quote.discount.toFixed(2)} before tax`}
                  </Text>
                </div>
                <Text size="lg" weight={700} style={{ color: 'var(--primary-gold)' }}>
                  -${quote.discount.toFixed(2)}
                </Text>
              </Group>
            </Alert>
          ) : (
            <>
              {!showCouponInput ? (
                <Button
                  variant="subtle"
                  size="sm"
                  onClick={() => setShowCouponInput(true)}
                  style={{ 
                    color: 'var(--primary-gold)',
                    padding: '8px 12px'
                  }}
                >
                  Have a promo code?
                </Button>
              ) : (
                <Stack spacing="sm">
                  <Group spacing="sm" align="flex-start">
                    <TextInput
                      placeholder="Enter promo code (optional)"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      style={{ flex: 1 }}
                      error={couponError}
                      disabled={couponLoading}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleApplyCoupon();
                        }
                      }}
                    />
                    <Button
                      onClick={handleApplyCoupon}
                      loading={couponLoading}
                      disabled={couponLoading || !couponCode.trim()}
                      variant="light"
                    >
                      Apply
                    </Button>
                    <Button
                      onClick={() => {
                        setShowCouponInput(false);
                        setCouponCode('');
                        setCouponError(null);
                      }}
                      variant="subtle"
                      color="gray"
                    >
                      Cancel
                    </Button>
                  </Group>
                </Stack>
              )}
            </>
          )}
        </div>

        <Checkbox
          mb="md"
          color="gold"
          checked={agreedToRefundPolicy}
          onChange={(e) => setAgreedToRefundPolicy(e.currentTarget.checked)}
          label={<>I have read and agree to the{' '}
            <a
              href="#refund-policy"
              style={{ color: 'var(--deep-gold)', fontWeight: 600 }}
              onClick={(e) => {
                // A link inside the label would also tick the box; just scroll to the policy.
                e.preventDefault();
                e.stopPropagation();
                document.getElementById('refund-policy')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
            >
              refund policy
            </a>.</>}
          styles={{ label: { textAlign: 'left' } }}
        />

        <Button
          type="submit"
          fullWidth
          size="lg"
          loading={processing}
          disabled={!quote || !card || processing || belowMinimum || !agreedToRefundPolicy}
          style={{
            background: !quote || !card || processing || belowMinimum || !agreedToRefundPolicy
              ? 'var(--light-gray)'
              : 'linear-gradient(135deg, var(--primary-gold), var(--deep-gold))',
            fontSize: '1.1rem',
            fontWeight: 600,
            minHeight: '56px',
            height: 'auto',
            padding: '14px 24px'
          }}
        >
          {processing
            ? 'Processing...'
            : !quote
              ? 'Calculating total...'
              : belowMinimum
                ? 'Total must be at least $1.00'
                : `Pay $${total.toFixed(2)} CAD`}
        </Button>

        {/* Security Message */}
        <Alert color="blue" icon={<IconLock size={16} />} variant="light">
          <Text size="xs" style={{ textAlign: 'left', lineHeight: 1.5 }}>
            <strong>Payments are secure.</strong> Prix du Canada does not store your payment information.
          </Text>
        </Alert>
      </Stack>
    </form>
  );
};

export default SquarePaymentForm;
